import { useState, useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ref, onValue, off, push, remove, update } from "firebase/database";
import { db } from "../../configs/Firebase";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import PersonaCard from "./components/PersonaCard";
import PersonaDetail from "./PersonaDetail";
import style from "./Personas.module.css";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Input from "../../designSystem/b-molecules/formElements/input/Input";

const Personas = () => {
  const navigate = useNavigate();
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [personas, setPersonas] = useState([]);
  const [filteredPersonas, setFilteredPersonas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);

  // Get current user
  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  // Load personas from Firebase
  useEffect(() => {
    if (org?.payload) {
      const personasRef = ref(db, `userData/${org.payload}/personas`);
      
      const unsubscribe = onValue(personasRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const personasList = Object.keys(data)
            .map(id => ({
              id,
              ...data[id]
            }))
            .filter(p => !p.archived) // Filter out archived personas
            .sort((a, b) => {
              // Sort by updatedAt or createdAt (newest first)
              const aTime = a.updatedAt || a.createdAt || 0;
              const bTime = b.updatedAt || b.createdAt || 0;
              return bTime - aTime;
            });
          setPersonas(personasList);
          setFilteredPersonas(personasList);
        } else {
          setPersonas([]);
          setFilteredPersonas([]);
        }
      });

      return () => {
        off(personasRef);
      };
    }
  }, [org?.payload]);

  // Filter personas based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPersonas(personas);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = personas.filter(persona => {
      const nameMatch = persona.name?.toLowerCase().includes(query);
      const taglineMatch = persona.tagline?.toLowerCase().includes(query);
      const tagsMatch = persona.tags?.some(tag => tag.toLowerCase().includes(query));
      const segmentMatch = persona.demographics?.segment?.toLowerCase().includes(query);
      const roleMatch = persona.demographics?.role?.toLowerCase().includes(query);
      
      return nameMatch || taglineMatch || tagsMatch || segmentMatch || roleMatch;
    });
    
    setFilteredPersonas(filtered);
  }, [searchQuery, personas]);

  const handleCreate = () => {
    navigate("/personas/new");
  };

  const handleEdit = (persona) => {
    navigate(`/personas/${persona.id}`);
  };

  const handleDuplicate = async (persona) => {
    if (!org?.payload || !window.confirm(`Duplicate "${persona.name}"?`)) {
      return;
    }

    try {
      const newPersona = {
        ...persona,
        name: `${persona.name} (Copy)`,
        createdAt: { ".sv": "timestamp" },
        updatedAt: { ".sv": "timestamp" },
        createdBy: user?.uid || null
      };
      delete newPersona.id;
      delete newPersona.photo; // Will need to copy photo separately if needed

      const personasRef = ref(db, `userData/${org.payload}/personas`);
      await push(personasRef, newPersona);
    } catch (error) {
      console.error("Error duplicating persona:", error);
      alert("Failed to duplicate persona");
    }
  };

  const handleDelete = async (persona) => {
    if (!org?.payload || !window.confirm(`Archive "${persona.name}"? This will hide it from the list but preserve links.`)) {
      return;
    }

    try {
      const personaRef = ref(db, `userData/${org.payload}/personas/${persona.id}`);
      await update(personaRef, { archived: true, updatedAt: { ".sv": "timestamp" } });
    } catch (error) {
      console.error("Error archiving persona:", error);
      alert("Failed to archive persona");
    }
  };


  return (
    <Routes>
      <Route path="/">
        <Route
          index
          element={
            <div className={style.personas}>
              <TitleBar
                title="Personas"
                actions={[
                  {
                    icon: "add_circle",
                    name: "New Persona",
                    action: handleCreate,
                    variants: ["text"]
                  }
                ]}
                breadcrumb={[{ name: "Home", url: "/" }]}
              />
              <div className={style.contentPadding}>
                <div className={style.searchContainer}>
                  <Input
                    type="text"
                    label="Search personas"
                    placeholder="Search by name, tagline, tags, segment, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                {filteredPersonas.length > 0 ? (
                  <div className={style.cardsContainer}>
                    {filteredPersonas.map((persona) => (
                      <PersonaCard
                        key={persona.id}
                        persona={persona}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                ) : personas.length === 0 ? (
                  <div className={style.emptyState}>
                    <Icon>person_add</Icon>
                    <h3>No personas yet</h3>
                    <p>Create your first persona to represent key user types</p>
                    <Button onClick={() => navigate("/personas/new")}>
                      <Icon>add</Icon> Create Persona
                    </Button>
                  </div>
                ) : (
                  <div className={style.emptyState}>
                    <Icon>search_off</Icon>
                    <h3>No personas found</h3>
                    <p>Try adjusting your search query</p>
                  </div>
                )}
              </div>

            </div>
          }
        />
        <Route path="new" element={<PersonaDetail />} />
        <Route path=":id" element={<PersonaDetail />} />
      </Route>
    </Routes>
  );
};

export default Personas;
